import { Injectable } from '@angular/core';

import { FixtureType, OfferCategory } from '@cds/betting-offer';
import { CountsFixturesResponse } from '@cds/betting-offer/domain-specific';
import { Competition, TagType } from '@cds/betting-offer/tags';
import { CountsFixturesRequest, FixtureState, OfferMapping, ParticipantMapping, SortByCriteria } from '@cds/query-objects';
import { EventModel } from '@frontend/sports/betting-offer/feature/model';
import { BettingOfferApi } from '@frontend/sports/betting-offer/feature/offer-service';
import { RegionConstant, SportConstant } from '@frontend/sports/common/core/data-access/constants';
import { CountItem, LeagueItem, mapNode } from '@frontend/sports/common/core/data-access/sport-model';
import { orderBy } from 'lodash-es';
import { Observable, map } from 'rxjs';

import { BaseTreeItem, GroupedEvents, LeftNavType, NavEventModel, TreeItemType } from '../../../event-details-common/event-details-common.models';
import { NavigationEventMapperService } from '../../navigation-mapper/navigation-event-mapper.service';

@Injectable()
export class EventSwitcherDataService {
    constructor(
        private bettingOfferApi: BettingOfferApi,
        private eventMapperService: NavigationEventMapperService,
    ) {}

    getEventsTree(event: EventModel, navType: LeftNavType): Observable<BaseTreeItem | undefined> {
        const selectedLeagueId = event.league.realCompetitionId ?? event.league.parentLeagueId ?? event.league.id;

        return this.getData(navType, event.sport.id, event.region.id, selectedLeagueId).pipe(
            map((data) => (data ? this.mapData(data, navType, event, selectedLeagueId) : undefined)),
        );
    }

    private getData(type: LeftNavType, sportId: number, regionId: number, leagueId: number): Observable<CountsFixturesResponse | undefined> {
        const request: CountsFixturesRequest = {
            fixtureCountsRequest: {
                state: type === LeftNavType.Live ? FixtureState.Live : FixtureState.Latest,
                tagTypes: [TagType.Region, TagType.Tournament, TagType.Competition].join(','),
                extendedTags: [TagType.Region, TagType.Tournament, TagType.Competition].join(','),
                fixtureCategories: OfferCategory.Gridable,
                excludedFixtureCategories: [OfferCategory.Outrights].join(','),
                competitionIds: type === LeftNavType.Competition ? leagueId.toString() : undefined,
                sportIds: sportId.toString(),
                regionIds: type === LeftNavType.Prematch && sportId !== SportConstant.Tennis ? regionId.toString() : undefined,
                tournamentIds: type === LeftNavType.Prematch && sportId === SportConstant.Tennis ? regionId.toString() : undefined,
                sortBy: SortByCriteria.Tags,
                isPriceBoost: false,
            },
            fixtureRequest: {
                state: type === LeftNavType.Live ? FixtureState.Live : FixtureState.Latest,
                fixtureTypes: FixtureType.Standard,
                fixtureCategories: OfferCategory.Gridable,
                excludedFixtureCategories: [OfferCategory.Outrights].join(','),
                competitionIds: type === LeftNavType.Competition ? leagueId.toString() : undefined,
                sportIds: sportId.toString(),
                regionIds: type === LeftNavType.Prematch && sportId !== SportConstant.Tennis ? regionId.toString() : undefined,
                tournamentIds: type === LeftNavType.Prematch && sportId === SportConstant.Tennis ? regionId.toString() : undefined,
                isPriceBoost: false,
                offerMapping: OfferMapping.None,
                participantMapping: ParticipantMapping.ExcludePlayer,
                sortBy: SortByCriteria.FixtureStage,
            },
        };

        return this.bettingOfferApi.getCountsFixtures(request);
    }

    private mapData(data: CountsFixturesResponse, type: LeftNavType, event: EventModel, selectedLeagueId: number): BaseTreeItem {
        const competitions = data.counts.filter((c) => c.tag.type === TagType.Competition);
        const regions =
            event.sport.id === SportConstant.Tennis
                ? data.counts.filter((c) => c.tag.type === TagType.Tournament)
                : data.counts.filter((c) => c.tag.type === TagType.Region);

        const competitionWithRegion = competitions.map((competition) => {
            const parentId =
                event.sport.id === SportConstant.Tennis ? (<Competition>competition.tag).parentTournamentId : (<Competition>competition.tag).parentId;
            const region = regions.find((p) => p.tag.id === parentId);

            return { competition, region };
        });

        const counts = orderBy(competitionWithRegion, [(c) => c.region?.rank, (c) => c.competition.rank, (c) => c.competition.tag.name.value]).map(
            (cr) => mapNode(cr.competition),
        );

        const tree = this.mapTree(counts, type, event, selectedLeagueId);

        return this.createTreeWithEvents(tree, this.eventMapperService.mapFixtures(data.fixtures), event.id);
    }

    private mapTree(leagues: CountItem[], type: LeftNavType, event: EventModel, selectedLeagueId: number): BaseTreeItem {
        return {
            children: leagues.map((l) => ({
                id: (<LeagueItem>l).siblings[0] ?? l.id,
                sportId: event.sport.id,
                name: l.name,
                type: TreeItemType.League,
                selected: selectedLeagueId === ((<LeagueItem>l).siblings[0] ?? l.id),
                expanded: true,
                loading: false,
                icon: 'country-icon c' + (event.sport.id === SportConstant.Tennis ? RegionConstant.World : l.parentId),
                class: 'league',
                children: [],
                events: [],
                collapsible: true,
                hideFavorites: true,
                isVirtual: l.isVirtual,
                virtualCompetitionId: l.isVirtual ? l.id : undefined,
                leftNavType: type,
            })),
        };
    }

    private createTreeWithEvents(tree: BaseTreeItem, groupedEvents: GroupedEvents, selectedEventId: string): BaseTreeItem {
        if (!groupedEvents) {
            return tree;
        }

        tree.children
            .filter((league) => !!groupedEvents[league.virtualCompetitionId ?? league.id])
            .forEach((league) => {
                const mappedEvents = this.mapNavEvents(groupedEvents[league.virtualCompetitionId ?? league.id], selectedEventId);
                league.events = mappedEvents;
                league.count = league.events.length;
            });

        return tree;
    }

    private mapNavEvents(events: EventModel[], selectedEventId?: string): NavEventModel[] {
        return events.map(
            (e) =>
                <NavEventModel>{
                    data: e,
                    showMarkets: false,
                    selected: e.id === selectedEventId,
                },
        );
    }
}
